import { FilesApi } from '../openapi/apis/FilesApi';
import { ApiGetFileUrlsRequestActionEnum } from '../openapi/models/ApiGetFileUrlsRequest';
import { createAuthenticatedApiClient } from '../helpers/api-client';
import { QUERY_STRING_STORE } from '@/helpers/constants';

/**
 * Service for handling file operations using the new Files API
 */
class FileService {
  constructor(cacheTTL = 60 * 60 * 1000) {
    this._fileUrlCache = {};
    // Time-to-live for cached URLs in milliseconds (defaults to 5 minutes)
    this._cacheTTL = cacheTTL;
  }

  /**
   * Update the cache TTL (Time-to-live) programmatically at runtime
   * @param {number} milliseconds - New TTL value in milliseconds
   */
  setCacheTTL(milliseconds) {
    this._cacheTTL = milliseconds;
  }

  /**
   * Determines whether the cached value for the provided key is still valid
   * @param {string} cacheKey - The internal cache key (including action)
   * @returns {boolean}
   */
  _isCacheValid(cacheKey) {
    const entry = this._fileUrlCache[cacheKey];
    return Boolean(entry && entry.expiresAt > Date.now());
  }

  /**
   * Gets a fresh instance of the Files API client with current auth token
   * @returns {FilesApi} - Authenticated Files API client
   */
  _getFilesApi() {
    return createAuthenticatedApiClient(FilesApi);
  }

  /**
   * Gets the current tenant ID from the session
   * @returns {string|null} - The tenant ID or null if not available
   */
  _getTenantId() {
    try {
      let tenant = JSON.parse(sessionStorage.getItem(QUERY_STRING_STORE.SELECT_BOX_TENANT))?.[0];
      if (tenant) {
        return tenant;
      }

      try {
        const tenant = JSON.parse(new URLSearchParams(window.location.hash.split('?')[1])?.get('select-tenant'))?.[0];
        if (tenant) {
          return tenant;
        }
      } catch (error) {
        console.error('Error retrieving tenant ID from url:', error);
      }

      tenant = JSON.parse(sessionStorage.getItem('tenants'))?.[0]?.tenant_id;
      return tenant
    } catch (error) {
      console.error('Error retrieving tenant ID from session:', error);
      return null;
    }
  }

  /**
   * Formats a file key to include tenant information
   * @param {string} fileKey - The original file key (bucketName/filePath)
   * @param {string|null} overrideTenantId - Optional tenant ID to override the one from session
   * @returns {string} - Formatted file key (bucketName/tenant-{tenantId}/filePath)
   */
  _formatFileKey(fileKey, overrideTenantId = null) {
    if (!fileKey) return fileKey;

    const tenantId = overrideTenantId || this._getTenantId();
    if (!tenantId) return fileKey;

    const parts = fileKey.split('/', 2);
    if (parts.length < 2) return fileKey;

    const bucketName = parts[0];
    const filePath = fileKey.substring(bucketName.length + 1);
    if (filePath.startsWith('icon/')) {
      return `${bucketName}/${filePath}`;
    }

    return `${bucketName}/tenant-${tenantId}/${filePath}`;
  }

  /**
   * Gets download URL for a file by key
   * @param {string} fileKey - The file key
   * @param {string|null} tenantId - Optional tenant ID to override the one from session
   * @param {string} action - The action type ('GET' for download, 'PUT' for upload), defaults to 'GET'
   * @returns {Promise<string>} - The download URL
   */
  async getFileUrl(fileKey, tenantId = null, action = 'GET') {
    try {
      const formattedKey = this._formatFileKey(fileKey, tenantId);

      // Create cache key that includes action
      const cacheKey = `${formattedKey}:${action}`;

      // Check cache first
      const cachedEntry = this._fileUrlCache[cacheKey];
      if (cachedEntry && cachedEntry.expiresAt > Date.now()) {
        return cachedEntry.url;
      }
      // Remove expired entry (if any)
      delete this._fileUrlCache[cacheKey];

      // Get fresh API client with current auth token
      const filesApi = this._getFilesApi();

      // Use the action parameter, converting to lowercase enum values
      const actionEnum = action.toLowerCase() === 'put' ? ApiGetFileUrlsRequestActionEnum.put : ApiGetFileUrlsRequestActionEnum.get;

      const response = await filesApi.getFileUrls({
        apiGetFileUrlsRequest: {
          action: actionEnum,
          keys: [formattedKey]
        }
      });

      if (response?.urls?.length > 0) {
        const url = response.urls[0].url;
        // Cache the result with action-specific key and expiry timestamp
        this._fileUrlCache[cacheKey] = { url, expiresAt: Date.now() + this._cacheTTL };
        return url;
      }
      throw new Error(`No URL returned for file key: ${formattedKey}`);
    } catch (error) {
      console.error('Error getting file URL:', error);
      throw error;
    }
  }

  /**
   * Gets multiple file URLs in a single request
   * @param {string[]} fileKeys - Array of file keys
   * @param {string|null} tenantId - Optional tenant ID to override the one from session
   * @param {string} action - The action type ('GET' for download, 'PUT' for upload), defaults to 'GET'
   * @returns {Promise<Object>} - Object mapping file keys to URLs
   */
  async getFileUrls(fileKeys, tenantId = null, action = 'GET') {
    try {
      // Create mapping from formatted keys to all original keys that map to them
      const formattedToOriginalKeys = {};
      const uniqueFormattedKeys = [];

      fileKeys.forEach(key => {
        const formattedKey = this._formatFileKey(key, tenantId);
        if (!formattedToOriginalKeys[formattedKey]) {
          formattedToOriginalKeys[formattedKey] = [];
          uniqueFormattedKeys.push(formattedKey);
        }
        formattedToOriginalKeys[formattedKey].push(key);
      });

      // Filter out keys that are already in cache **and still valid**
      const keysToFetch = uniqueFormattedKeys.filter(key => {
        const cacheKey = `${key}:${action}`;
        return !this._isCacheValid(cacheKey);
      });

      if (keysToFetch.length === 0) {
        // Return cached results for all keys
        return fileKeys.reduce((acc, key) => {
          const formattedKey = this._formatFileKey(key, tenantId);
          const cacheKey = `${formattedKey}:${action}`;
          const cachedEntry = this._fileUrlCache[cacheKey];
          if (cachedEntry && cachedEntry.expiresAt > Date.now()) {
            acc[key] = cachedEntry.url;
          }
          return acc;
        }, {});
      }

      // Get fresh API client with current auth token
      const filesApi = this._getFilesApi();

      // Use the action parameter, converting to lowercase enum values
      const actionEnum = action.toLowerCase() === 'put' ? ApiGetFileUrlsRequestActionEnum.put : ApiGetFileUrlsRequestActionEnum.get;

      const response = await filesApi.getFileUrls({
        apiGetFileUrlsRequest: {
          action: actionEnum,
          keys: keysToFetch
        }
      });

      // Convert response to map of key->url, using original keys
      const result = {};

      // First add cached values for all original keys
      fileKeys.forEach(key => {
        const formattedKey = this._formatFileKey(key, tenantId);
        const cacheKey = `${formattedKey}:${action}`;
        const cachedEntry = this._fileUrlCache[cacheKey];
        if (cachedEntry && cachedEntry.expiresAt > Date.now()) {
          result[key] = cachedEntry.url;
        } else {
          delete this._fileUrlCache[cacheKey];
        }
      });

      // Then add new values from API and update cache
      if (response?.urls?.length > 0) {
        response.urls.forEach(item => {
          // Get all original keys that map to this formatted key
          const originalKeys = formattedToOriginalKeys[item.key] || [];

          // Set URL for all original keys that map to this formatted key
          originalKeys.forEach(originalKey => {
            result[originalKey] = item.url;
          });

          // Cache with action-specific key and expiry timestamp
          this._fileUrlCache[`${item.key}:${action}`] = { url: item.url, expiresAt: Date.now() + this._cacheTTL };
        });
      }

      return result;
    } catch (error) {
      console.error('Error getting multiple file URLs:', error);
      throw error;
    }
  }

  /**
   * Clear the URL cache
   */
  clearCache() {
    this._fileUrlCache = {};
  }
}

// Create a singleton instance
const fileService = new FileService();

export default fileService; 
